import React, {useEffect, useState} from 'react';
import * as Yup from 'yup';
import {compose} from 'redux';
import {connect} from 'react-redux';

import './styles.scss';
import ModalTemplate from '../../../../../modal';
import {
  AppForm,
  AppFormDatePickerField,
  AppFormField,
  AppFormSubmitButton,
} from '../../../../../forms';
import {Column, Row} from '../../../../../layout';
import {
  has18Years,
  today,
  getDateInServerFormat,
} from '../../../../../../util/utility';
import * as fonts from '../../../../../../util/fonts';
import {
  addToGlobalStyles,
  addToUser,
  changeUserDOB,
  setAvailableBus,
  setBusFuncMap,
  setBusMap,
  setBusStylesMap,
  setIban,
  setSelectedBu,
  setTitular,
  setUser,
} from '../../../../../../store/actions';
import {get} from '../../../../../../api/api';
import {
  uriAccountSaveData,
  uriCard,
  uriGetBus,
  uriGetTitular,
} from '../../../../../../api/endpoints';
import Icon from '../../../../../icon';
import {Icons} from '../../../../../../util/icons';
import colors from '../../../../../../util/colors';
import AppButton from '../../../../../app-button';
import {strings} from '../../../../../../util/strings';
import {useHistory} from 'react-router-dom';
import DataNotFoundModal from '../data-not-found-modal/DataNotFoundModal';
import ConfirmPasswordModal from '../confirm-password-modal/ConfirmPasswordModal';
import AppErrorMessage from '../../../../../app-error-message';

interface InsertNewDataModalProps {
  show: boolean;
  setShow(value: boolean): void;
  user: any;
  labels: typeof strings.pt.virtualCard;
  labelsMessages: typeof strings.pt.messages;
  setBusStylesMap(value: any): void;
  setAvailableBus(value: any): void;
  setBusMap(value: any): void;
  setSelectedBu(value: any): void;
  addToUser(value: any): void;
  changeUserDOB(value: any): void;
  setUser(value: any): void;
  busStyles: any;
  busFunctionalities: any;
  bankAccountDataHolder: any;
}

const validationSchema = Yup.object().shape({
  dateOfBirth: Yup.date().required().max(today(true)),
  cardNumber: Yup.string().required().max(20),
});

function NewInsertNewDataModal({
  show,
  setShow,
  user,
  labels,
  labelsMessages,
  setBusStylesMap,
  setAvailableBus,
  setBusMap,
  setSelectedBu,
  addToUser,
  changeUserDOB,
  setUser,
  busStyles,
  busFunctionalities,
  bankAccountDataHolder,
}: InsertNewDataModalProps) {
  const [valid, setValid] = useState(false);
  const [minorUser, setMinorUser] = useState(false);
  const [formikContext, setFormikContext] = useState();
  const [dataNotFound, setDataNotFound] = useState(false);
  const [generalError, setGeneralError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dob, setDob] = useState<string>('');
  const [showDataNotFound, setShowDataNotFound] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hoverErrorIcons, setHoverErrorIcons] = useState<any>({
    insuranceNumber: false,
    dateOfBirth: false,
  });
  const history = useHistory();
  const featureName = 'VirtualCard-InsertDataV2';

  const defineOnMouseFunctions = (
    e: React.MouseEvent,
    fieldNamePar: string,
    dateInput?: boolean
  ) => {
    var elem: HTMLElement = e.target as HTMLElement;
    if (!dateInput) {
      var icon: HTMLElement = elem.nextSibling as HTMLElement;
      defineIconFunctions(icon, fieldNamePar);
    }

    if (
      dateInput &&
      elem.tagName.includes('svg') &&
      elem.className['baseVal' as any].includes('error')
    ) {
      var icon: HTMLElement = elem as HTMLElement;
      defineIconFunctions(icon, fieldNamePar);
    }
  };

  const defineIconFunctions = (icon: HTMLElement, fieldNamePar: any) => {
    if (icon) {
      icon.onmouseover = function () {
        setHoverErrorIcons({...hoverErrorIcons, [fieldNamePar]: true});
      };
      icon.onmouseout = function () {
        setHoverErrorIcons({...hoverErrorIcons, [fieldNamePar]: false});
      };
    }
  };

  const onSubmit = async (values: any) => {
    setSubmitting(true);
    // if the user has at least 18 years continues
    if (has18Years(values.dateOfBirth)) {
      // if the user inserts valid data the same logic as in the login page will be applyed
      try {
        // getting user bus
        const userBusResponse: any = await get(
          uriAccountSaveData(
            user.nif,
            getDateInServerFormat(values.dateOfBirth),
            values.cardNumber
          )
        );

        const busResponse: any = await get(uriGetBus());
        const bus = busResponse?.data?.items;
        const busMap: any = {};

        // creating a new map with the bus and cards from the response
        userBusResponse.forEach((elem: any) => {
          const famId = elem.famId;
          const buId = elem.buId;
          const existingBu = !!busMap[buId];

          const member = {...elem};
          const memberBu = bus.find((bu: any) => bu.id === buId);
          member.buName = (memberBu && memberBu.name) || '';

          if (existingBu) {
            // if the bu already exists in the map: checks for existing groups in map
            const existingGroup = !!busMap[buId][famId];

            if (existingGroup) {
              // if the group already exists in the map: adds the current member to the preexisting group
              busMap[buId][famId].push(member);
            } else {
              // otherwise: adds the new group to the map prefilled with the current member
              busMap[buId][famId] = [member];
            }
          } else {
            // otherwise: adds the new bu prefilled with the current member's group
            busMap[buId] = {[`${famId}`]: [member]};
          }
        });

        // for each of the users's bu adds a new key to the bus styles map with the correnponding bu style returned from the BO calls
        let busStylesMap: any = {};

        Object.keys(busMap).forEach((key: string, index: number) => {
          busStylesMap[key] = busStyles[index];
        });
        setBusStylesMap(busStylesMap);

        // for each of the users's bu adds a new key to the bus func map with the correnponding bu functionalities returned from the BO calls
        let busFuncMap: any = {};
        Object.keys(busMap).forEach(
          (key: string, index: number) =>
            (busFuncMap[key] = busFunctionalities[index].funcionalidade)
        );
        setBusFuncMap(busFuncMap);

        // for each card/member of each user's bu obtains the card data and adds it to the busMap
        await Promise.all(
          Object.keys(busMap).flatMap((buId) => {
            return Object.keys(busMap[buId]).map((famId) => {
              return Promise.all(
                busMap[buId][famId].map((member: any, index: number) => {
                  return get(
                    uriCard(member.buId, member.memId, member.famId),
                    false,
                    0,
                    featureName
                  )
                    .then((buUserCard: any) => {
                      // const buUserIndex = busMap[buId][
                      //   famId
                      // ].indexOf(member);
                      busMap[buId][famId][index].card = buUserCard?.data;
                    })
                    .catch((e: any) => {
                      const buUserIndex = busMap[buId][famId].indexOf(member);

                      // if the card api call fails or returns nothing sets the card as {}
                      busMap[buId][famId][buUserIndex].card = {};
                    });
                })
              );
            });
          })
        );

        const auxBus = Object.values(
          Object.values(Object.values(busMap))
        ).flatMap((group: any) => Object.values(group).flatMap((elem) => elem));

        // sets the card/member with selectedProduct as true as the selected card/member/bu in redux
        const newSelectedBu: any =
          auxBus.find((elem: any) => elem.selectedProduct) || auxBus[0];

        setBusMap(busMap);

        // saves all info to redux
        setAvailableBus(userBusResponse);
        updateDobRedux();
        setSelectedBu(newSelectedBu);

        if (bankAccountDataHolder === 'SUBSCRIBER') {
          try {
            let titular: any = await get(
              uriGetTitular(newSelectedBu.buId, newSelectedBu.famId)
            );
            if (titular) {
              setTitular(titular);
              setIban(titular.iban);
            } else {
              setTitular(null);
              setIban(null);
            }
          } catch (err: any) {
            setTitular(null);
            setIban(null);
          }
        }

        addToUser({
          state: 'CLIENT',
        });
        history.push('/callback');
      } catch (e: any) {
        if (e.status === 404) {
          const newUser = {
            ...user,
            hasMemId: true,
            hasNewData: true,
          };
          setUser(newUser);
          setShowDataNotFound(true);
        } else {
          setGeneralError(true);
        }
      } finally {
        setSubmitting(false);
      }
    } //otherwise, displays the minor user error message
    else {
      setMinorUser(true);
      setSubmitting(false);
    }
  };

  const updateDobRedux = () => {
    if (dob) {
      var dobDate = new Date(dob);
      var day = dobDate?.getDate();
      var month = dobDate?.getMonth() + 1;
      var year = dobDate?.getFullYear();

      var dobString =
        year +
        '-' +
        (month < 10 ? '0' + month : month) +
        '-' +
        (day < 10 ? '0' + day : day);

      changeUserDOB(dobString);
    }
  };

  useEffect(() => {
    if (formikContext) {
      (formikContext as any)?.validateForm((formikContext as any)?.values);
    }
  }, [formikContext]);

  return (
    <>
      <ModalTemplate
        show={show}
        cross={true}
        onHide={() => {
          setShow(false);
          setMinorUser(false);
          setDataNotFound(false);
        }}
        hideFooter={true}
        centered={false}>
        {minorUser ? (
          <Column>
            <Row className="insert-new-data-modal-row insert-new-data-modal-title insert-new-data-modal-text">
              {labels?.changeInsuranceData_title}
            </Row>

            <Row className="insert-new-data-modal-icon-row">
              <Icon
                width="32"
                height="32"
                color={colors.alertRed}
                name={Icons.ALERT_PROBLEM}
                changeViewbox
                viewBox={'0 0 24 24'}
              />
            </Row>
            <Row
              className="insert-new-data-modal-row"
              style={{...fonts.MM, color: colors.alertRed}}>
              {labels?.changeInsuranceData_ageError_title}
            </Row>
            <Row className="insert-new-data-modal-row insert-new-data-age-error-row">
              {labels?.changeInsuranceData_ageError_text}
            </Row>
            <AppButton
              className="insert-new-data-modal-error-button"
              variant="secondary"
              onClick={() => {
                setShow(false);
                setMinorUser(false);
              }}>
              {labels?.changeInsuranceData_ageError_button_web}
            </AppButton>
          </Column>
        ) : (
          <Column>
            <Row className="insert-new-data-modal-row insert-new-data-modal-title insert-new-data-modal-title-text">
              {labels?.changeInsuranceData_title}
            </Row>
            <Row
              className="insert-new-data-modal-row insert-new-data-modal-text"
              style={{...fonts.M}}>
              {labels?.changeInsuranceData_subTitle}
            </Row>
            <div className="line-separator-top"></div>
            <div className="insert-nif-text-container">
              <span
                style={{
                  ...fonts.M,
                  ...fonts.Bold,
                }}>{`${labels?.changeInsuranceData_insertedNif}: ${user.nif}`}</span>
            </div>
            <div>
              <span
                style={{
                  ...fonts.M,
                }}>
                {labels?.changeInsuranceData_wrongNif}{' '}
                <a
                  style={{color: colors.blue}}
                  onClick={() => setShowConfirmPassword(true)}>
                  {labels?.changeInsuranceData_newNif}
                </a>
              </span>
            </div>
            <div className="line-separator-bottom"></div>
            <Row
              className="insert-new-data-modal-row insert-new-data-modal-text insert-new-data-modal-subtitle-row"
              style={{...fonts.M}}>
              {labels?.changeInsuranceData_subTitleBottom}
            </Row>

            <AppForm
              initialValues={{
                dateOfBirth: '',
                buId: '',
                cardNumber: '',
              }}
              validationSchema={validationSchema}
              onSubmit={(values) => onSubmit(values)}
              onValidation={(valid) => setValid(valid)}
              onChange={(values) => setDob(values.dateOfBirth)}>
              <Row
                className="insert-new-data-modal-row insert-new-data-modal-field-row"
                onMouseOver={(e) => {
                  defineOnMouseFunctions(e, 'dateOfBirth', true);
                }}>
                <AppFormDatePickerField
                  name="dateOfBirth"
                  placeholder={labels?.changeInsuranceData_birthDatePlacehoder}
                  handleFormikContext={(formik) => {
                    if (
                      !!!formikContext ||
                      formik.errors !== (formikContext as any).errors
                    ) {
                      setFormikContext(formik);
                    }
                  }}
                  onMouseOut={() => {
                    setHoverErrorIcons({
                      ...hoverErrorIcons,
                      ['dateOfBirth']: false,
                    });
                  }}
                />
                {hoverErrorIcons.dateOfBirth && (
                  <AppErrorMessage
                    message={
                      (formikContext &&
                        (formikContext as any).errors &&
                        (formikContext as any).errors.code) ||
                      labelsMessages?.requiredField
                    }
                    className="insert-new-data-field-error-date"
                  />
                )}
              </Row>
              <Row className="insert-new-data-modal-row insert-new-data-modal-field-row">
                <AppFormField
                  name="cardNumber"
                  placeholder={
                    labels?.changeInsuranceData_insuranceNumberPlaceholder
                  }
                  handleFormikContext={(formik) => {
                    if (
                      !!!formikContext ||
                      formik.errors !== (formikContext as any).errors
                    ) {
                      setFormikContext(formik);
                    }
                  }}
                  onMouseOver={(e) => {
                    defineOnMouseFunctions(e, 'insuranceNumber');
                  }}
                />
                {hoverErrorIcons.insuranceNumber && (
                  <AppErrorMessage
                    message={
                      (formikContext &&
                        (formikContext as any).errors &&
                        (formikContext as any).errors.code) ||
                      labelsMessages?.requiredField
                    }
                    className="insert-new-data-field-error"
                  />
                )}
              </Row>

              <AppFormSubmitButton
                title={labels?.changeInsuranceData_button}
                disabled={!valid || submitting || showDataNotFound}
                className="insert-new-data-modal-submit-button"
              />
            </AppForm>
          </Column>
        )}
      </ModalTemplate>
      <DataNotFoundModal
        showDataNotFound={showDataNotFound}
        setShowDataNotFound={setShowDataNotFound}
      />
      <ConfirmPasswordModal
        show={showConfirmPassword}
        setShow={setShowConfirmPassword}
      />
    </>
  );
}

const mapStateToProps = ({
  user,
  stringsDefault,
  busStyles,
  busFunctionalities,
  bankAccountDataHolder,
}: any) => ({
  user: user.value,
  labels: stringsDefault.value.pt.virtualCard,
  labelsMessages: stringsDefault.value.pt.messages,
  busStyles: busStyles.value,
  busFunctionalities: busFunctionalities.value,
  bankAccountDataHolder: bankAccountDataHolder.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  setBusStylesMap: (value: any) => dispatch(setBusStylesMap(value)),
  setAvailableBus: (value: any) => dispatch(setAvailableBus(value)),
  setBusMap: (value: any) => dispatch(setBusMap(value)),
  setSelectedBu: (value: any) => dispatch(setSelectedBu(value)),
  addToUser: (value: any) => dispatch(addToUser(value)),
  setBusFuncMap: (value: any) => dispatch(setBusFuncMap(value)),
  addToGlobalStyles: (value: any) => dispatch(addToGlobalStyles(value)),
  changeUserDOB: (value: any) => dispatch(changeUserDOB(value)),
  setUser: (value: any) => dispatch(setUser(value)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)(NewInsertNewDataModal)
);
